/** 信用卡单笔凭证识别：拍照/图片/PDF → 填入当前草稿 → 财务补充并保存。
 * 识别结果仅作建议，不直接创建交易或标记银行核对。原件仅在当前组件内保留用于失败重试。
 */
import React, { useEffect, useRef, useState } from "react";
import { Camera, FileUp, Loader2, RotateCcw } from "lucide-react";
import { Button } from "../../components/ui/button";

const fieldLabels = { date: ["日期", "date"], kind: ["类型", "type"], amount: ["金额", "amount"], merchant: ["商户", "merchant"], category: ["费用类别", "category"], purpose: ["用途", "purpose"] };
const warningLabels = {
  currency_unknown: ["币种未确认，金额需手动填写。", "Currency is unclear; enter the USD amount manually."],
  currency_mismatch: ["凭证不是美元，金额未自动填写，请核对美元记账金额。", "This is not a USD document. Check and enter the USD amount."],
  uncertain: ["部分内容不确定，请对照凭证核对。", "Some details are uncertain. Check the original document."],
};

/** cardId 指定录入账户；onRecognized 接收白名单建议，父表单负责保护用户已修改的字段。 */
export default function CreditCardTransactionImport({ zh, api, cardId, disabled, onBusy, onRecognized }) {
  const camera = useRef(null), picker = useRef(null), controller = useRef(null);
  const [file, setFile] = useState(null), [busy, setBusy] = useState(false);
  const [error, setError] = useState(""), [result, setResult] = useState(null);
  useEffect(() => () => controller.current?.abort(), []);

  /** 每次只发一个凭证；关闭后忽略迟到响应，失败保留原文件与手填草稿。 */
  const scan = async (document) => {
    if (disabled || controller.current || !document) return;
    const request = new AbortController(); controller.current = request;
    setBusy(true); onBusy(true); setError(""); setResult(null);
    try {
      const body = new FormData(); body.append("file", document);
      const { data } = await api.post(`/finance/credit-cards/${cardId}/transactions/recognize`, body, { signal: request.signal, timeout: 75000 });
      if (request.signal.aborted) return;
      onRecognized(data.fields || {}); setResult(data); setFile(null);
    } catch (e) {
      if (!request.signal.aborted) setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail :
        (zh ? "识别失败，请重试、更换凭证或手动填写。" : "Recognition failed. Retry, choose another document, or enter manually."));
    } finally {
      if (!request.signal.aborted) { controller.current = null; setBusy(false); onBusy(false); }
    }
  };

  /** 拍照沿用手机原生后置相机；图片与PDF均在本地先检查大小，服务端再校验真实内容。 */
  const choose = (event) => {
    const document = event.target.files?.[0]; event.target.value = "";
    if (!document || disabled || controller.current) return;
    setFile(null); setResult(null);
    if (!/\.(jpe?g|png|webp|heic|heif|pdf)$/i.test(document.name) || !document.size || document.size > 10 * 1024 * 1024) {
      setError(zh ? "请选择 10 MB 以内的 JPG、PNG、WEBP、HEIC 图片或 PDF。" : "Choose a JPG, PNG, WEBP, HEIC or PDF up to 10 MB."); return;
    }
    setFile(document); scan(document);
  };

  return <section className="rounded-xl border border-border bg-muted/50 p-3 space-y-2" aria-busy={busy} data-testid="cc-tx-recognition">
    <div className="flex flex-wrap gap-2">
      <Button type="button" variant="outline" size="sm" disabled={disabled || busy} data-testid="cc-tx-camera-btn" onClick={() => camera.current?.click()}><Camera className="h-4 w-4 mr-1.5" aria-hidden="true" />{zh ? "拍照识别" : "Scan receipt"}</Button>
      <Button type="button" variant="outline" size="sm" disabled={disabled || busy} data-testid="cc-tx-document-btn" onClick={() => picker.current?.click()}><FileUp className="h-4 w-4 mr-1.5" aria-hidden="true" />{zh ? "图片 / PDF" : "Image / PDF"}</Button>
    </div>
    <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={choose} data-testid="cc-tx-camera" />
    <input ref={picker} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf" className="hidden" onChange={choose} data-testid="cc-tx-document" />
    <p className="text-xs text-muted-foreground">{zh ? "上传单笔凭证，自动填写明细并建议类别、用途，可手动修改和补充。" : "Upload one transaction to fill details and suggest a category and purpose. You can edit and complete all fields."}</p>
    <p className="text-xs text-muted-foreground">{zh ? "最多 10 MB，PDF 最多 10 页；文件仅用于 AI 识别，不保存为附件。" : "Up to 10 MB / 10 PDF pages. Files are sent for AI recognition, not saved as attachments."}</p>
    {busy && <p role="status" className="flex items-center gap-1.5 text-sm text-foreground"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />{zh ? "正在读取凭证，请稍候…" : "Reading document…"}</p>}
    {result && <div role="status" className="space-y-1 text-sm text-foreground" data-testid="cc-tx-recognition-result">
      <p>{zh ? "识别完成，已保留手填内容，请核对并补充后保存。" : "Document read. Manual entries were kept. Review and complete the details before saving."}</p>
      {!!result.missing?.length && <p className="text-xs text-muted-foreground">{zh ? "未识别项，请确认已补充：" : "Missing details to check: "}{result.missing.map((key) => fieldLabels[key]?.[zh ? 0 : 1]).filter(Boolean).join(zh ? "、" : ", ")}</p>}
      {(result.warnings || []).filter((key) => warningLabels[key]).map((key) => <p key={key} className="text-xs text-foreground">{warningLabels[key][zh ? 0 : 1]}</p>)}
    </div>}
    {error && <div className="space-y-2"><p role="alert" className="text-sm text-destructive" data-testid="cc-tx-recognition-error">{error}</p>
      {file && <Button type="button" variant="outline" size="sm" disabled={disabled || busy} onClick={() => scan(file)} data-testid="cc-tx-recognition-retry"><RotateCcw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />{zh ? "重新识别" : "Retry"}</Button>}
    </div>}
  </section>;
}
